// Vue
import { mixins } from "vue-class-component";
import { Vue, Component, Prop, Watch } from "vue-property-decorator";

// Template
import template from "./template.vue";

// HereMaps
/* import "./heremaps/js/mapsjs-core.js";
import "./heremaps/js/mapsjs-service.js";
import "./heremaps/js/mapsjs-ui.js";
import "./heremaps/js/mapsjs-mapevents.js"; */
import H from "./heremaps/mapsjs.bundle.js";
import { EventBus } from "../../../mixins/event_bus/EventBus";
import { HttpService } from "../../../services/HttpService";

@Component({
  name: "Map",
  mixins: [template],
})
export class Map extends mixins(EventBus) {
  private map!: H.Map;
  private platform!: H.service.Platform;
  private marker: H.map.Marker | null = null;

  private markerIcon: H.map.Icon = new H.map.Icon(
    `${APP_ASSETS_URL}images/custom/svg/icon-138__location--default.svg`,
    { size: { w: 29, h: 40 }, crossOrigin: false }
  );

  private readonly http: HttpService = HttpService.instance;

  private addressLat: number | null = null;
  private addressLng: number | null = null;

  private clickedLat: number | null = null;
  private clickedLng: number | null = null;

  @Prop({ default: "48.784294" })
  public lat!: number;

  @Prop({ default: "9.242289" })
  public lng!: number;

  @Prop({ default: 15 })
  public zoom!: number;

  @Prop({ default: true })
  public controls!: boolean;

  @Prop({ default: "350px" })
  public height!: string;

  @Prop({ default: "100%" })
  public width!: string;

  @Prop()
  public address!: string;

  @Prop()
  public name!: string;

  @Prop({ default: false })
  public clickable!: boolean;

  @Prop({ default: null })
  public customMarkerIcon!: H.map.Icon | null;

  @Prop({ default: APP_MAP_API_KEY || "${HERE_MAP_API_KEY}" })
  public apikey!: string;

  /* @Prop({ default: "MkC2PRYjW5teZTkDFtD1" })
  public appId!: string;

  @Prop({ default: "IEGY4xN1K2wry4y6-jcrWQ" })
  public appCode!: string; */

  get finalMarkerIcon() {
    if (this.customMarkerIcon) {
      return this.customMarkerIcon;
    }

    return this.markerIcon;
  }

  get finalLat() {
    if (this.addressLat) {
      return this.addressLat;
    } else if (this.clickedLat) {
      return this.clickedLat;
    } else {
      return this.lat;
    }
  }

  get finalLng() {
    if (this.addressLng) {
      return this.addressLng;
    } else if (this.clickedLng) {
      return this.clickedLng;
    } else {
      return this.lng;
    }
  }

  @Watch("address")
  private async watchAddress(newAddress, oldAddress) {
    await this.findAddressLatLng();
  }

  @Watch("finalLat")
  public onFinalLngChanges() {
    this.updateMarker();
  }

  @Watch("finalLng")
  public onFinalLatChanges() {
    this.updateMarker();
  }

  @Watch("finalMarkerIcon")
  public onMarkerIconChange() {
    if (this.marker) {
      this.marker.setIcon(this.finalMarkerIcon);
    }
  }

  public async mounted() {
    this.initMap();
    window.addEventListener("resize", this.handleWindowResize);
    this.$on("Modal.opened", this.handleWindowResize);
  }

  public destroyed() {
    window.removeEventListener("resize", this.handleWindowResize);
    this.$off("Modal.opened", this.handleWindowResize);
  }

  get fieldValue() {
    return "(" + this.finalLat + "," + this.finalLng + ")";
  }

  public initMap() {
    Vue.nextTick(async () => {
      if (this.map) {
        this.map.dispose();
      }

      // Connect to the API
      this.platform = new H.service.Platform({
        apikey: this.apikey,
        useCIT: true,
        useHTTPS: true,
      });

      const pixelRatio = window.devicePixelRatio || 1;

      // Create default Layer
      const defaultLayers = this.platform.createDefaultLayers({
        tileSize: pixelRatio === 1 ? 256 : 512,
        ppi: pixelRatio === 1 ? undefined : 320,
      });

      // Init Map
      this.map = new H.Map(<Element>this.$refs.map, defaultLayers.vector.normal.map, {
        zoom: this.zoom,
        center: { lat: this.lat, lng: this.lng },
        pixelRatio: pixelRatio,
      });

      if (this.controls) {
        // Create UI Controllers
        const ui = H.ui.UI.createDefault(this.map, defaultLayers);

        // Allow zoom with mouse wheel and drag
        const behavior = new H.mapevents.Behavior(new H.mapevents.MapEvents(this.map));
      }

      if (this.finalLat !== null && this.finalLng !== null) {
        const marker = new H.map.Marker({ lat: this.finalLat, lng: this.finalLng }, { icon: this.finalMarkerIcon });
        this.map.addObject(marker);
        this.map.setCenter({ lat: this.finalLat, lng: this.finalLng });
        this.marker = marker;
      }

      this.map.addEventListener("tap", this.onMapClick);

      if (this.address && this.lat == 0 && this.lng == 0) {
        await this.findAddressLatLng();
      }
    });
  }

  private onMapClick(event) {
    if (this.clickable) {
      var coord = this.map.screenToGeo(event.currentPointer.viewportX, event.currentPointer.viewportY);

      this.clickedLat = coord.lat;
      this.clickedLng = coord.lng;
      this.addressLat = null;
      this.addressLng = null;

      this.$emit("input", coord);
    }
  }

  private updateMarker() {
    if (this.marker && this.finalLat && this.finalLng) {
      this.marker.setGeometry({ lat: this.finalLat, lng: this.finalLng });
      this.map.setCenter({ lat: this.finalLat, lng: this.finalLng });
    }
  }

  public handleWindowResize() {
    if (this.map) {
      this.map.getViewPort().resize();
    }
  }

  public async findAddressLatLng() {
    try {
      const response = await this.http.get<any>("https://geocode.search.hereapi.com/v1/geocode", {
        params: {
          apiKey: this.apikey,
          q: this.address,
        },
      });

      const result = response.body?.items[0];
      this.onCorrectAddress(result);
    } catch (error) {
      console.log(error);
      this.onIncorrectAddress(error);
    }
  }

  public onCorrectAddress(result: any) {
    if (result) {
      const locations = result.position;

      this.addressLat = locations.lat;
      this.addressLng = locations.lng;
      this.clickedLat = null;
      this.clickedLng = null;
      this.$emit("input", { lat: this.addressLat, lng: this.addressLng });
    } else {
      this.addressLat = null;
      this.addressLng = null;
    }
  }

  public onIncorrectAddress(error) {
    this.addressLat = null;
    this.addressLng = null;
    this.$emit("error", error);
  }
}
